import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextSeo } from 'next-seo';
import type { ReactElement } from 'react';
import React from 'react';
import styled from 'styled-components';

import { richTextToReactNode } from '@seuk/cms-api-client';
import { Typography, Container } from '@seuk/design-system/atoms';
import { Breadcrumbs } from '@seuk/design-system/molecules';

import { Main } from '../../components';
import TextPageItemComponent from '../../components/text-page-item';

import PageItemSection from './components/section';
import type { PageProps, TrackInfo } from './types';

const Page = styled(Main)`
  padding-top: ${({ theme }) => theme.spacing(30)};
  padding-bottom: ${({ theme }) => theme.spacing(37)};
  position: relative;
`;

const PageItem = styled(TextPageItemComponent)`
  max-width: 100%;

  ${({ theme }) => theme.breakpoints.up('lg')} {
    max-width: ${({ columnAmount }) => (columnAmount ? '100%' : '60%')};
  }
`;

function InfoPage<GetStaticPropsType extends GetStaticProps<PageProps & { trackInfo: TrackInfo }>>({
  sections,
  title,
  description,
  trackInfo,
  seo,
  slug,
}: InferGetStaticPropsType<GetStaticPropsType>): ReactElement {
  const breadcrumbItems = React.useMemo(() => {
    return [
      { label: 'Home', href: '/' },
      slug.startsWith('/policies/')
        ? {
            label: 'Policies',
            href: '/policies',
          }
        : null,
      { label: title },
    ].filter((value) => value !== null);
  }, [title, slug]);

  return (
    <>
      <NextSeo title={seo?.title} description={seo?.description} nofollow={seo?.nofollow} noindex={seo?.noindex} />
      <Page>
        <Container>
          <Breadcrumbs items={breadcrumbItems} backHref={breadcrumbItems[breadcrumbItems.length - 2].href} />
          <Typography variant="h1" weight="bold" notch={true} component="h1">
            {title}
          </Typography>
          {description ? richTextToReactNode(description) : null}
          {sections.map((section) => (
            <PageItem key={section.id} title={section.title} columnAmount={section.columnAmount}>
              <PageItemSection section={section} trackInfo={trackInfo} />
            </PageItem>
          ))}
        </Container>
      </Page>
    </>
  );
}

export function makePage<GetStaticPropsType extends GetStaticProps<PageProps & { trackInfo: TrackInfo }>>(
  trackInfo?: TrackInfo
): React.FC<InferGetStaticPropsType<GetStaticProps<PageProps>>> {
  return function CustomInfoPage({ sections, title, description, seo, slug }) {
    const defaultTrackInfo: TrackInfo = {
      pageTitle: title,
    };

    return (
      <InfoPage<GetStaticPropsType>
        sections={sections}
        title={title}
        description={description}
        seo={seo}
        slug={slug}
        trackInfo={trackInfo ?? defaultTrackInfo}
      />
    );
  };
}
